// @react
import React from 'react'
import { useState } from 'react'
// @common
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { contentTypes, ContentType } from 'constants/contentTypesMap'
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery'
import Helper from 'utils/Helper.class'
// @components
import colors from '@bit/atd.web.colors'
import Box from '@bit/atd.web.box'
import Divider from '@bit/atd.web.divider'
import Grid from '@bit/atd.web.grid'
import Button from '@bit/atd.web.button'
import Modal from '@bit/atd.web.modal'
// @algolia core
import {
  connectCurrentRefinements,
  Configure,
  connectHierarchicalMenu,
} from 'react-instantsearch-dom'
// @algolia widgets
import Pagination from '../Widgets/Pagination'
import ClearRefinements from '../Widgets/ClearRefinements'
import CurrentRefinements from '../Widgets/CurrentRefinements'
import HierarchicalMenu from '../Widgets/HierachicalMenu'
import SortBy from '../Widgets/SortBy'
import SearchResults from '../Results'
import Filters from '../Filters'
import sortByIndexes from 'modules/search/config/sortBy'
import Breadcrumb from '../Widgets/Breadcrumb'
import ClearFilters from '../Widgets/ClearFilters'
import { EntityTypes } from '.././../config/facets'
// @graphql
import { GetUserQuery, GetUserQueryVariables } from 'lib/graphql/graphqlTypes'
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
import resultSets from '../../config/resultSets'
import CustomStats from '../Widgets/Stats'
import ButtonDropdown from '@bit/atd.web.button-dropdown'

// @ts-ignore
const CustomClearRefinements = connectCurrentRefinements(ClearRefinements)
// @ts-ignore
const CustomClearFilters = connectCurrentRefinements(ClearFilters)
// @ts-ignore
const CustomCurrentRefinements = connectCurrentRefinements(CurrentRefinements)
const CustomHierarchicalMenu = connectHierarchicalMenu(HierarchicalMenu)

const attributes = [
  'contentTypes.lvl0',
  'contentTypes.lvl1',
  'contentTypes.lvl2',
]

const useStyles = makeStyles({
  clearBtnWrapper: {
    display: 'flex',
    alignItems: 'center',
    margin: '0px 16px 8px 0px',
  },
  filterBlock: {
    marginBottom: '38px',
  },
  sidebarTitle: {
    marginBottom: '20px',
  },
  center: {
    textAlign: 'center',
  },
})
/**********************************
 *  TYPES
 **********************************/
type PropsType = {
  index: string
  baseIndex: string
}

enum FilterModals {
  FILTERS = 'Filters',
  CATEGORIES = 'Categories',
}

const Search = (props: PropsType) => {
  /**********************************
   *  PROPS
   **********************************/
  const { index, baseIndex } = props

  /**********************************
   *  STATE
   **********************************/
  const [isNoResults, setIsNoResults] = useState(false)
  const [modalFilterType, setModalFilterType] = useState<FilterModals | null>(
    null,
  )
  const { data } = useQuery<GetUserQuery, GetUserQueryVariables>(GET_USER)
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('xs'))
  const classes = useStyles()
  const sortBy = sortByIndexes.find((sortBy) => sortBy.index === baseIndex)
  const isMember = data?.user?.membership?.isMember
  const filterEntities = isMember
    ? [EntityTypes.Member, EntityTypes.Default]
    : [EntityTypes.Default]

  /**
   * we can change to controlled state by specifying initial state to indexSearch and then manually manipulating state
   */
  const configureSearch = (
    <Configure
      hitsPerPage={16}
      distinct={true}
      highlightPreTag={'<em>'}
      highlightPostTag={'</em>'}
      snippetEllipsisText={'...'}
    />
  )

  /**********************************
   *  FACET FILTERS
   **********************************/
  const contentTypeMenu = (
    <div>
      <div className={classes.sidebarTitle}>
        {index === 'master' ? (
          <h3>Categories</h3>
        ) : (
          <Breadcrumb attributes={attributes} />
        )}
      </div>

      <CustomHierarchicalMenu
        attributes={attributes}
        limit={5}
        showParentLevel={true}
        showMoreLimit={1000}
        showMore={!mobile}
        index={index}
        transformItems={(items: any) => {
          if (index === 'master') {
            return items
          }
          const contentType = contentTypes.find(
            (contentType: ContentType) => contentType.index === baseIndex,
          )
          return items.filter((item: any) => item.label === contentType?.label)
        }}
      />
    </div>
  )

  const selectedFilters = (
    <Grid container>
      <CustomCurrentRefinements
        transformItems={(items: any) => {
          return items
            .filter((item: any) => item.attribute !== 'contentTypes.lvl0')
            .map((item: any) => {
              if (item?.attribute === 'purchasableItems.beginDate') {
                const filters = item?.items.reduce(
                  (acc: any, beginDateItem: any) => {
                    const facetName =
                      beginDateItem.label === '0'
                        ? 'Anytime'
                        : Helper.getMonthAndYear(Number(beginDateItem.label))
                    return acc.some(({ label }: any) => label === facetName)
                      ? acc.map(({ label, refineValues = [] }: any) => ({
                          label,
                          refineValues: [...refineValues, beginDateItem.value],
                        }))
                      : [
                          ...acc,
                          {
                            label: facetName,
                            refineValues: [beginDateItem.value],
                          },
                        ]
                  },
                  [],
                )
                return { ...item, items: filters }
              }
              return item
            })
        }}
      />
      {!mobile && (
        <Grid className={classes.clearBtnWrapper}>
          <CustomClearRefinements clearsQuery />
        </Grid>
      )}
    </Grid>
  )
  const desktopFilters = (
    <Box>
      <Grid
        container
        direction="column"
        justify="flex-start"
        alignItems="stretch"
        spacing={2}
      >
        <Grid item>{contentTypeMenu}</Grid>
        <Grid item>
          <Divider color={colors.gray300} width={'100%'} height={'1px'} />
          <h3>Filters</h3>
        </Grid>
        <Filters id={index} entities={filterEntities} />
      </Grid>
    </Box>
  )
  const filtersModal = (
    <Modal
      onClose={() => setModalFilterType(null)}
      headerNoSeparator
      open={modalFilterType === FilterModals.FILTERS}
      fullScreen={true}
      title={FilterModals.FILTERS}
      keepMounted={true}
      actions={
        <Grid container>
          <Grid className={classes.center} xs={12} item>
            <CustomStats
              title={''}
              resultName={'Result'}
              indexes={
                index === 'master'
                  ? resultSets.map(({ index }) => index)
                  : undefined
              }
            />
          </Grid>
          <Grid className={classes.center} xs={6} item>
            <CustomClearFilters clearsQuery />
          </Grid>
          <Grid className={classes.center} item>
            <Button onClick={() => setModalFilterType(null)}>
              Show Results
            </Button>
          </Grid>
        </Grid>
      }
    >
      <>
        {selectedFilters}
        <Filters id={index} entities={filterEntities} />
      </>
    </Modal>
  )
  const categoriesModal = (
    <Modal
      onClose={() => setModalFilterType(null)}
      headerNoSeparator
      open={modalFilterType === FilterModals.CATEGORIES}
      fullScreen={true}
      title={FilterModals.CATEGORIES}
      keepMounted={true}
      actions={
        <Grid container>
          <Grid className={classes.center} xs={12} item>
            <CustomStats
              title={''}
              resultName={'Result'}
              indexes={
                index === 'master'
                  ? resultSets.map(({ index }) => index)
                  : undefined
              }
            />
          </Grid>
          <Grid className={classes.center} xs={12} item>
            <Button
              fullWidth={true}
              alignment="center"
              onClick={() => setModalFilterType(null)}
            >
              Show Results
            </Button>
          </Grid>
        </Grid>
      }
    >
      {contentTypeMenu}
    </Modal>
  )
  const mobileFilters = (
    <>
      <Grid container spacing={2}>
        <Grid xs={6} item>
          <ButtonDropdown
            fullWidth={true}
            size="medium"
            open={false}
            onClick={() => setModalFilterType(FilterModals.FILTERS)}
          >
            {FilterModals.FILTERS}
          </ButtonDropdown>
        </Grid>
        <Grid xs={6} item>
          <ButtonDropdown
            fullWidth={true}
            size="medium"
            open={false}
            onClick={() => setModalFilterType(FilterModals.CATEGORIES)}
          >
            {FilterModals.CATEGORIES}
          </ButtonDropdown>
        </Grid>
      </Grid>
      {filtersModal}
      {categoriesModal}
    </>
  )

  /**********************************
   *  RENDER
   **********************************/
  return (
    <>
      {configureSearch}
      <Grid container spacing={mobile ? 2 : 10}>
        <Grid item xs={12} md={4}>
          {mobile ? mobileFilters : desktopFilters}
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid
            container
            className={classes.filterBlock}
            justify={'space-between'}
            alignItems={'center'}
          >
            <Grid item lg={sortBy ? 9 : 12} xs={sortBy ? 8 : 12}>
              {!mobile && selectedFilters}
            </Grid>
            {sortBy && (
              <Grid item xs={12} lg={2} md={3}>
                <Grid container justify="flex-end">
                  <SortBy
                    fullWidth={mobile}
                    items={sortBy.values}
                    defaultRefinement={
                      sortBy.values.find((sortBy) => sortBy.value === index)
                        ?.value || sortBy.values[0].value
                    }
                  />
                </Grid>
              </Grid>
            )}
          </Grid>

          <Grid container>
            <SearchResults
              index={index}
              baseIndex={baseIndex}
              isNoResults={isNoResults}
              setIsNoResults={setIsNoResults}
            />
          </Grid>

          {index !== 'master' && !isNoResults && (
            <Grid item xs={12}>
              <Pagination />
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  )
}

export default Search
